import React, { useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import useEventoForm from "../../hooks/useEventoForm";
import { MODO_CREAR } from "../../domain/evento/schema";
import { toPayload } from "../../domain/evento/payload";
import { hayErrores } from "../../domain/evento/validacion";
import NameSection from "./secciones/NameSection";
import ImageSection from "./secciones/ImageSection";
import DateSection from "./secciones/DateSection";
import DetailsSection from "./secciones/DetailsSection";
import SponsorsSection from "./secciones/SponsorsSection";
import "../../views/eventos/evento/evento.css";

/* Formulario de evento, único para crear y editar.
 *
 * `onSubmit` recibe el payload ya armado y devuelve true si la operación salió
 * bien. Toda la diferencia entre los dos modos está en los valores iniciales y
 * en qué hace ese callback: las secciones, la validación y el armado del
 * payload son exactamente los mismos. */
const EventoForm = ({
  modo = MODO_CREAR,
  valoresIniciales,
  onSubmit,
  textoSubmit = "Guardar",
  textoEnviando = "Guardando...",
  deshabilitado = false,
  children,
}) => {
  const {
    form,
    errores,
    setCampo,
    setFecha,
    agregarFecha,
    eliminarFecha,
    validar,
  } = useEventoForm(valoresIniciales);

  const [seccionAbierta, setSeccionAbierta] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const alternarSeccion = (id) =>
    setSeccionAbierta((actual) => (actual === id ? null : id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;

    setEnviando(true);
    try {
      await onSubmit(toPayload(form), form);
    } finally {
      setEnviando(false);
    }
  };

  const propsSeccion = (id) => ({
    errores,
    abierta: seccionAbierta === id,
    onToggle: alternarSeccion,
  });

  return (
    <div className="contenedor-evento">
      {/* el id trae el centrado de las cards (evento.css) */}
      <form id="form-evento" onSubmit={handleSubmit} noValidate>
        <ImageSection media={form.media} onChange={setCampo} {...propsSeccion("img")} />

        <NameSection
          evento={form.evento}
          descripcion={form.descripcion}
          onChange={setCampo}
          {...propsSeccion("gral")}
        />

        <DateSection
          form={form}
          modo={modo}
          onChange={setCampo}
          onFechaChange={setFecha}
          onAgregarFecha={agregarFecha}
          onEliminarFecha={eliminarFecha}
          {...propsSeccion("date")}
        />

        <DetailsSection
          tipoId={form.eventos_tipos_id}
          privado={form.privado}
          onChange={setCampo}
          {...propsSeccion("detail")}
        />

        <SponsorsSection
          sponsors={form.sponsors}
          onChange={setCampo}
          {...propsSeccion("sponsors")}
        />

        {hayErrores(errores) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Por favor, revisá los campos marcados en rojo antes de continuar.
          </Alert>
        )}

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button type="submit" variant="contained" disabled={enviando || deshabilitado}>
            {enviando && <CircularProgress size={18} sx={{ mr: 1, color: "white" }} />}
            {enviando ? textoEnviando : textoSubmit}
          </Button>
          {children}
        </div>
      </form>
    </div>
  );
};

export default EventoForm;
